import Image from "next/image";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePathname, useRouter } from "next/navigation";

import * as Schema from "../../types/schema";
import Block from "../../components/block";
import { Colorways } from "src/theme";
import { Heart } from "./Heart";
import Link from "next/link";
import { uuid } from "../../utils/uuid";

type Props = { hero: Schema.PricingLandingHero; colorway: Colorways };
export function AdoptionPlansHero({ colorway, hero }: Props) {
  const currentPagePath = usePathname();
  const router = useRouter();

  // memos
  /////
  const defaultSwitchOptions = useMemo(() => {
    if (isEmpty(hero?.switch?.options)) return [];

    return hero.switch.options.map((option, i) => {
      return {
        ...option,
        _id: option._id || uuid(), // Use existing id or create a new one
        _isSelected: option.linkTo.includes(currentPagePath),
      };
    });
  }, [hero]);

  // states
  /////
  const [switchOptions, setSwitchOptions] = useState(defaultSwitchOptions);

  // callbacks
  /////
  const findSwitchOption = useCallback(
    (option: { _id: string }) => {
      return switchOptions.find((opt) => opt._id === option._id);
    },
    [switchOptions],
  );

  const switchOptionEmphasis = useCallback(
    (option: { _id: string }) => {
      const foundSwitchOption = findSwitchOption(option);
      return foundSwitchOption?._isSelected
        ? `bg-default-gradient text-white`
        : "text-onyx";
    },
    [colorway, findSwitchOption],
  );

  const navigateTo = useCallback(
    (option: { linkTo: string }) => () => {
      router.push(option.linkTo);
    },
    [router],
  );

  return (
    <Block fullWidth flushBottom flushTop className="mt-[80px]">
      <div className="bg-families-gradient relative flex min-h-[480px] w-full items-center pb-12 pt-6 md:px-16 lg:py-12">
        <div className="absolute bottom-0 left-0 top-[60%] z-0 h-full w-full max-w-[450px]">
          <Image
            src="/hero-vector-overlay.png"
            width={0}
            height={0}
            style={{ width: "100%" }}
            className={`flex w-full`}
            alt="hero"
          />
        </div>

        <div className="relative z-10 grid w-full grid-cols-none grid-rows-[auto,1fr,auto] lg:grid-cols-[1fr,fit-content(100%),1fr] lg:grid-rows-none">
          <div />

          <div className="mt-6 p-6 text-center lg:p-0">
            {!!hero.overline && (
              <p className="text-overline-1 text-white">{hero.overline}</p>
            )}

            {!!hero.heading && (
              <h1 className="text-deco-head-1 text-white">
                <strong>
                  <em>{hero.heading}</em>
                </strong>
              </h1>
            )}

            <div className="mt-12 flex flex-col">
              {!isEmpty(hero?.switch) && (
                <>
                  {!!hero?.switch.title && (
                    <p className="text-head-6 text-white">
                      {hero?.switch.title}
                    </p>
                  )}

                  <div className="mt-4 grid h-[60px] w-auto auto-cols-auto grid-flow-col items-center gap-1 rounded-[80px] border border-platinum bg-platinum-light p-1">
                    {switchOptions?.map((option) => (
                      <div
                        key={option._id}
                        className={`${switchOptionEmphasis(
                          option,
                        )} text-body-3 flex h-full cursor-pointer items-center rounded-full px-6 font-semibold text-onyx`}
                        onClick={navigateTo(option)}
                      >
                        <p>{option.label}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center justify-center lg:mt-0 lg:items-end xl:mr-[13%]">
            <div className="text-center">
              {hero?.note?.text && (
                <p className="text-handwriting-3 max-w-[250px] text-center text-white">
                  {hero?.note?.text}
                </p>
              )}

              {!isEmpty(hero?.note?.callout) && (
                <div className="flex flex-row items-center justify-center gap-2">
                  <Link
                    href={hero?.note?.callout.linkTo}
                    className="text-body-3 flex flex-row text-white underline decoration-solid hover:text-teal"
                  >
                    {hero?.note?.callout.label}
                    <Heart className={`h-auto w-full max-w-[24px]`} />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Block>
  );
}
